import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../config/api'; // Assuming BASE_URL is correctly defined in your config/api.js
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf'; // Import jsPDF
import 'jspdf-autotable'; // Import for table support
import * as XLSX from 'xlsx'; // Import xlsx for Excel export

const JobworkList = () => {
    // State for jobworks and filtered jobworks
    const [jobworks, setJobworks] = useState([]);
    const [filteredJobworks, setFilteredJobworks] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    // Fetch jobworks when the component mounts
    useEffect(() => {
        fetch(`${BASE_URL}jobwork/`)
            .then(response => response.json())
            .then(data => {
                if (data.status === 1) {
                    setJobworks(data.data); // Store the jobworks data
                    setFilteredJobworks(data.data); // Set initial filtered jobworks
                } else {
                    alert('Error fetching jobworks');
                }
            })
            .catch(error => console.error('Error fetching jobworks:', error));
    }, []);

    // Handle search term change
    const handleSearchChange = (event) => {
        const term = event.target.value;
        setSearchTerm(term);

        // Filter jobworks based on the search term
        const filtered = jobworks.filter(jobwork =>
            jobwork.description.toLowerCase().includes(term.toLowerCase())
        );
        setFilteredJobworks(filtered);
    };

    // Navigate to the edit jobwork page
    const handleEdit = (id) => {
        navigate(`/EditJobwork/${id}`);
    };

    // Handle jobwork deletion
    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this jobwork?")) {
            fetch(`${BASE_URL}jobwork/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then(response => response.json())
                .then(data => {
                    if (data.status === 1) {
                        // Remove the deleted jobwork from both lists (jobworks and filteredJobworks)
                        setJobworks(jobworks.filter(jobwork => jobwork.id !== id));
                        setFilteredJobworks(filteredJobworks.filter(jobwork => jobwork.id !== id));
                    }
                })
                .catch(error => console.error('Error deleting jobwork:', error));
        }
    };

    // Navigate to the add jobwork page
    const handleAdd = () => {
        navigate('/AddJobwork');
    };

    // Export filtered jobworks to PDF
    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Add Title
        doc.text('Jobwork List', 14, 10); // Title at top of PDF

        // Prepare table data for PDF
        const tableData = filteredJobworks.map(jobwork => [
            jobwork.id,
            jobwork.description || 'N/A',
            jobwork.image ? `${BASE_URL}${jobwork.image}` : 'No Image',
            new Date(jobwork.created_at).toLocaleString(),
            new Date(jobwork.updated_at).toLocaleString(),
        ]);

        // Add table to PDF with autoTable
        doc.autoTable({
            head: [['ID', 'Description', 'Image', 'Created At', 'Updated At']], // Table headers
            body: tableData,
        });

        // Save the PDF with a filename
        doc.save('jobwork_list.pdf');
    };

    // Export filtered jobworks to Excel
    const handleExportExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredJobworks.map(jobwork => ({
            ID: jobwork.id,
            Description: jobwork.description || 'N/A',
            Image: jobwork.image ? `${BASE_URL}${jobwork.image}` : 'No Image',
            CreatedAt: new Date(jobwork.created_at).toLocaleString(),
            UpdatedAt: new Date(jobwork.updated_at).toLocaleString(),
        })));
        
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Jobwork List");

        // Save the Excel file with a filename
        XLSX.writeFile(workbook, 'jobwork_list.xlsx');
    };

    return (
        <div id="main">
            <header className="mb-3">
                <a href="#" className="burger-btn d-block d-xl-none">
                    <i className="bi bi-justify fs-3"></i>
                </a>
            </header>

            <div className="page-heading">
                <div className="page-title">
                    <div className="row">
                        <div className="col-12 col-md-6 order-md-1 order-last">
                            <h3>Jobwork List</h3>
                        </div>
                        <div className="col-12 col-md-6 order-md-2 order-first">
                            <nav aria-label="breadcrumb" className="breadcrumb-header float-start float-lg-end">
                                <ol className="breadcrumb">
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            {/* Jobwork Table */}
            <section className="section">
                <div className="row" id="basic-table">
                    <div className="col-12 col-md-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="row">
                                    <div className="col-md-6">
                                    </div>
                                    <div className="col-md-6 text-end">
                                        <button className="btn btn-primary" onClick={handleAdd}>Add Jobwork</button>
                                        {/* Export to PDF Button */}
                                        <button className="btn btn-secondary ms-2" onClick={handleExportPDF}>Export to PDF</button>
                                        {/* Export to Excel Button */}
                                        <button className="btn btn-secondary ms-2" onClick={handleExportExcel}>Export to Excel</button>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label htmlFor="searchTerm" className="form-label">Search by Description</label>
                                    <input
                                        type="text"
                                        id="searchTerm"
                                        className="form-control"
                                        placeholder="Search jobwork description"
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                    />
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-lg">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Description</th>
                                                <th>Image</th>
                                                {/* <th>Created At</th>
                                                <th>Updated At</th> */}
                                                <th className="text-end">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredJobworks.map(jobwork => (
                                                <tr key={jobwork.id}>
                                                    <td>{jobwork.id}</td>
                                                    <td>{jobwork.description || 'N/A'}</td>
                                                    <td>
                                                        <img
                                                            src={`${BASE_URL}${jobwork.image}`}
                                                            alt={jobwork.description}
                                                            width="100"
                                                            height="100"
                                                        />
                                                    </td>
                                                    {/* <td>{new Date(jobwork.created_at).toLocaleString()}</td>
                                                    <td>{new Date(jobwork.updated_at).toLocaleString()}</td> */}
                                                    <td className="text-end">
                                                        {/* Edit Button */}
                                                        <button className="btn btn-success" onClick={() => handleEdit(jobwork.id)}>Edit</button>
                                                        {/* Delete Button */}
                                                        <button className="btn btn-danger ms-2" onClick={() => handleDelete(jobwork.id)}>Delete</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default JobworkList;
